/**
 * MRT and LRT line colours plus a static station-name to line-codes map.
 * The station list is the network the hub pages in `src/lib/hubPages.ts`
 * (MRT_STATIONS) draw from, completed to the full network so any station
 * name the map tiles return resolves. Hard-coded on purpose: nothing here
 * parses the hub file at runtime.
 */

export type MrtLineCode = "NS" | "EW" | "CG" | "NE" | "CC" | "CE" | "DT" | "TE" | "BP" | "SK" | "PG";

export const MRT_LINES: Record<MrtLineCode, { name: string; colour: string }> = {
  NS: { name: "North South Line", colour: "#D42E12" },
  EW: { name: "East West Line", colour: "#009645" },
  CG: { name: "Changi Airport Branch", colour: "#009645" },
  NE: { name: "North East Line", colour: "#9900AA" },
  CC: { name: "Circle Line", colour: "#FA9E0D" },
  CE: { name: "Circle Line Extension", colour: "#FA9E0D" },
  DT: { name: "Downtown Line", colour: "#005EC4" },
  TE: { name: "Thomson-East Coast Line", colour: "#9D5B25" },
  BP: { name: "Bukit Panjang LRT", colour: "#748477" },
  SK: { name: "Sengkang LRT", colour: "#748477" },
  PG: { name: "Punggol LRT", colour: "#748477" },
};

export const DEFAULT_LINE_COLOUR = "#0A6FD4";

const STATION_LINES: Record<string, MrtLineCode[]> = {
  // North South Line
  "jurong east": ["NS", "EW"],
  "bukit batok": ["NS"],
  "bukit gombak": ["NS"],
  "choa chu kang": ["NS", "BP"],
  "yew tee": ["NS"],
  kranji: ["NS"],
  marsiling: ["NS"],
  woodlands: ["NS", "TE"],
  admiralty: ["NS"],
  sembawang: ["NS"],
  canberra: ["NS"],
  yishun: ["NS"],
  khatib: ["NS"],
  "yio chu kang": ["NS"],
  "ang mo kio": ["NS"],
  bishan: ["NS", "CC"],
  braddell: ["NS"],
  "toa payoh": ["NS"],
  novena: ["NS"],
  newton: ["NS", "DT"],
  orchard: ["NS", "TE"],
  somerset: ["NS"],
  "dhoby ghaut": ["NS", "NE", "CC"],
  "city hall": ["NS", "EW"],
  "raffles place": ["NS", "EW"],
  "marina bay": ["NS", "CE", "TE"],
  "marina south pier": ["NS"],
  // East West Line and the Changi branch
  "pasir ris": ["EW"],
  tampines: ["EW", "DT"],
  simei: ["EW"],
  "tanah merah": ["EW", "CG"],
  bedok: ["EW"],
  kembangan: ["EW"],
  eunos: ["EW"],
  "paya lebar": ["EW", "CC"],
  aljunied: ["EW"],
  kallang: ["EW"],
  lavender: ["EW"],
  bugis: ["EW", "DT"],
  "tanjong pagar": ["EW"],
  "outram park": ["EW", "NE", "TE"],
  "tiong bahru": ["EW"],
  redhill: ["EW"],
  queenstown: ["EW"],
  commonwealth: ["EW"],
  "buona vista": ["EW", "CC"],
  dover: ["EW"],
  clementi: ["EW"],
  "chinese garden": ["EW"],
  lakeside: ["EW"],
  "boon lay": ["EW"],
  pioneer: ["EW"],
  "joo koon": ["EW"],
  "gul circle": ["EW"],
  "tuas crescent": ["EW"],
  "tuas west road": ["EW"],
  "tuas link": ["EW"],
  expo: ["CG", "DT"],
  "changi airport": ["CG"],
  // North East Line
  harbourfront: ["NE", "CC"],
  chinatown: ["NE", "DT"],
  "clarke quay": ["NE"],
  "little india": ["NE", "DT"],
  "farrer park": ["NE"],
  "boon keng": ["NE"],
  "potong pasir": ["NE"],
  woodleigh: ["NE"],
  serangoon: ["NE", "CC"],
  kovan: ["NE"],
  hougang: ["NE"],
  buangkok: ["NE"],
  sengkang: ["NE", "SK"],
  punggol: ["NE", "PG"],
  "punggol coast": ["NE"],
  // Circle Line
  "bras basah": ["CC"],
  esplanade: ["CC"],
  promenade: ["CC", "DT"],
  "nicoll highway": ["CC"],
  stadium: ["CC"],
  mountbatten: ["CC"],
  dakota: ["CC"],
  macpherson: ["CC", "DT"],
  "tai seng": ["CC"],
  bartley: ["CC"],
  "lorong chuan": ["CC"],
  marymount: ["CC"],
  caldecott: ["CC", "TE"],
  "botanic gardens": ["CC", "DT"],
  "farrer road": ["CC"],
  "holland village": ["CC"],
  "one north": ["CC"],
  "kent ridge": ["CC"],
  "haw par villa": ["CC"],
  "pasir panjang": ["CC"],
  "labrador park": ["CC"],
  "telok blangah": ["CC"],
  keppel: ["CC"],
  cantonment: ["CC"],
  "prince edward road": ["CC"],
  bayfront: ["CE", "DT"],
  // Downtown Line
  "bukit panjang": ["DT", "BP"],
  cashew: ["DT"],
  hillview: ["DT"],
  hume: ["DT"],
  "beauty world": ["DT"],
  "king albert park": ["DT"],
  "sixth avenue": ["DT"],
  "tan kah kee": ["DT"],
  stevens: ["DT", "TE"],
  rochor: ["DT"],
  downtown: ["DT"],
  "telok ayer": ["DT"],
  "fort canning": ["DT"],
  bencoolen: ["DT"],
  "jalan besar": ["DT"],
  bendemeer: ["DT"],
  "geylang bahru": ["DT"],
  mattar: ["DT"],
  ubi: ["DT"],
  "kaki bukit": ["DT"],
  "bedok north": ["DT"],
  "bedok reservoir": ["DT"],
  "tampines west": ["DT"],
  "tampines east": ["DT"],
  "upper changi": ["DT"],
  // Thomson-East Coast Line
  "woodlands north": ["TE"],
  "woodlands south": ["TE"],
  springleaf: ["TE"],
  lentor: ["TE"],
  mayflower: ["TE"],
  "bright hill": ["TE"],
  "upper thomson": ["TE"],
  napier: ["TE"],
  "orchard boulevard": ["TE"],
  "great world": ["TE"],
  havelock: ["TE"],
  maxwell: ["TE"],
  "shenton way": ["TE"],
  "gardens by the bay": ["TE"],
  "marina south": ["TE"],
  "tanjong rhu": ["TE"],
  "katong park": ["TE"],
  "tanjong katong": ["TE"],
  "marine parade": ["TE"],
  "marine terrace": ["TE"],
  siglap: ["TE"],
  bayshore: ["TE"],
  // Bukit Panjang LRT
  "south view": ["BP"],
  "keat hong": ["BP"],
  "teck whye": ["BP"],
  phoenix: ["BP"],
  petir: ["BP"],
  pending: ["BP"],
  bangkit: ["BP"],
  fajar: ["BP"],
  segar: ["BP"],
  jelapang: ["BP"],
  senja: ["BP"],
  // Sengkang LRT
  compassvale: ["SK"],
  rumbia: ["SK"],
  bakau: ["SK"],
  kangkar: ["SK"],
  ranggung: ["SK"],
  "cheng lim": ["SK"],
  farmway: ["SK"],
  kupang: ["SK"],
  thanggam: ["SK"],
  fernvale: ["SK"],
  layar: ["SK"],
  tongkang: ["SK"],
  renjong: ["SK"],
  // Punggol LRT
  cove: ["PG"],
  meridian: ["PG"],
  "coral edge": ["PG"],
  riviera: ["PG"],
  kadaloor: ["PG"],
  oasis: ["PG"],
  damai: ["PG"],
  "sam kee": ["PG"],
  "teck lee": ["PG"],
  "punggol point": ["PG"],
  samudera: ["PG"],
  nibong: ["PG"],
  sumang: ["PG"],
  "soo teck": ["PG"],
};

/** "Dhoby Ghaut MRT Station" / "one-north" / "HarbourFront Interchange" -> a lookup key. */
function stationKey(name: string) {
  return name
    .toLowerCase()
    .replace(/\b(mrt|lrt|station|interchange)\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function linesForStation(name: string): MrtLineCode[] {
  return STATION_LINES[stationKey(name)] ?? [];
}

export function lineColour(code: string | undefined): string {
  return code && code in MRT_LINES ? MRT_LINES[code as MrtLineCode].colour : DEFAULT_LINE_COLOUR;
}
